// =====================================================================
// CONSTANTS: quiz.constants.ts
// FUNCTIONAL OVERVIEW:
//   Shared constants for the Quiz feature (JHUD-5314): batch size,
//   upload rules, chat topic and storage key prefix.
// =====================================================================
import { MAX_FILE_SIZE_BYTES } from '../../../../utility/constant-config';
import { QuizSuggestedAction } from '../../../models/quiz/quiz-api.models';

export const QUIZ_BATCH_SIZE = 10;
export const QUIZ_MAX_BATCH_SIZE = 20;
export const QUIZ_CHAT_TOPIC = 'quiz';
export const QUIZ_MENU_CODE = 'quiz';
export const QUIZ_DRAFT_STORAGE_PREFIX = 'quiz';
export const QUIZ_CHAT_HISTORY_PAGE_SIZE = 100; // backend maximum

export const QUIZ_ALLOWED_FILE_EXTENSIONS: readonly string[] = ['.pdf', '.docx', '.doc'];
export const QUIZ_ALLOWED_MIME_TYPES: readonly string[] = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
];
export const QUIZ_MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_BYTES;
export const QUIZ_MAX_FILES_PER_SESSION = 1; // confirm (§16 Q2)

export const QUIZ_GENERIC_ERROR_MESSAGE = "I couldn't generate the quiz right now. Please try again.";
export const QUIZ_DISABLED_MESSAGE = "Quiz isn't enabled for your platform.";
export const QUIZ_READY_MESSAGE = 'Your quiz is ready. Type your answer to the question below in the chat.';
export const QUIZ_SUBMIT_FAILED_MESSAGE = "Your answers are saved here but couldn't be submitted. Select Submit answers to try again.";
export const QUIZ_ANSWER_PLACEHOLDER = 'Type your answer here...';
export const QUIZ_BATCH_DECISION_PLACEHOLDER = 'Choose Continue with 10 more or End Quiz to continue.';
export const QUIZ_DOCUMENT_REQUEST = 'Generate a quiz document with answers.';
export const QUIZ_CONTINUE_REQUEST = `Continue with ${QUIZ_BATCH_SIZE} more`;
export const QUIZ_DOCUMENT_INTRO =
  "Choose what to include. I'll create the document here, then wait for you to choose a format and download it.";

const SET_ORDINALS: readonly string[] = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth'];

export const quizSetCompleteMessage = (batchNo: number): string =>
  `You completed the ${SET_ORDINALS[batchNo - 1] ?? `#${batchNo}`} set. ` +
  `The next set contains ${QUIZ_BATCH_SIZE} unique questions with no repeats.`;

// Orchestrator sends suggested actions as codes; answers are typed in chat, so no submit chip.
const QUIZ_SUGGESTED_ACTION_LABELS: Record<string, string> = {
  next_batch: 'Next 10',
  export_docx: 'Export as Word',
  export_pdf: 'Export as PDF',
  new_quiz: 'Start a new quiz',
};
const QUIZ_HIDDEN_SUGGESTED_ACTIONS: readonly string[] = ['submit_answers'];

// -----------------------------------------------------------------
// METHOD PURPOSE:
//   Maps orchestrator `suggested_actions` (codes or objects) to labelled
//   quick-reply chips, dropping unknown codes and hidden actions.
//
// MODIFICATION HISTORY (latest first – keep ONLY the last 3):
//   [JHUD-5314] 2026-10-29 20:16 | Vikas Kumar | Moved from QuizFacade so the history page renders the same chips.
// -----------------------------------------------------------------
export const toQuizSuggestedActions = (raw: Array<QuizSuggestedAction | string>): QuizSuggestedAction[] =>
  raw
    .map((entry) => (typeof entry === 'string' ? { action: entry, label: QUIZ_SUGGESTED_ACTION_LABELS[entry] ?? '' } : entry))
    .filter((entry) => !!entry.label?.trim() && !QUIZ_HIDDEN_SUGGESTED_ACTIONS.includes(entry.action));
